import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileSignature, AlertTriangle } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const fileUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export function ReceiptBody({ receipt, compact = false, testPrefix = "receipt" }) {
  return (
    <div className="space-y-3" data-testid={`${testPrefix}-body`}>
      <div className="text-xs text-zinc-600">Signed by <b className="text-[#0C2E2B]" data-testid={`${testPrefix}-signed-by`}>{receipt.signed_by}</b> on {new Date(receipt.signed_at).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</div>
      {receipt.notes && <p className="text-xs text-zinc-600 italic">"{receipt.notes}"</p>}
      {receipt.photo_urls?.length > 0 && (
        <div className={`grid gap-2 ${compact ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-3"}`} data-testid={`${testPrefix}-photos`}>
          {receipt.photo_urls.map((u, i) => (
            <a key={u} href={fileUrl(u)} target="_blank" rel="noreferrer" className="block rounded-md overflow-hidden border-2 border-zinc-200 aspect-square">
              <img src={fileUrl(u)} alt={`Drop-off ${i + 1}`} className="w-full h-full object-cover" data-testid={`${testPrefix}-photo-${i}`} />
            </a>
          ))}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Signature</div>
        <img src={fileUrl(receipt.signature_url)} alt="Customer signature" className={`bg-white border-2 border-zinc-200 rounded-md ${compact ? "max-h-20" : "max-h-32"}`} data-testid={`${testPrefix}-signature`} />
      </div>
    </div>
  );
}

export default function ReceiptPage() {
  const { ref } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { axios.get(`${API}/receipt/${ref}`).then(({ data }) => setData(data)).catch(() => setErr("We couldn't find a delivery receipt for that reference.")); }, [ref]);

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="receipt-page">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-bold text-[#0C2E2B] hover:underline"><ArrowLeft className="w-4 h-4" /> Home</Link>
        {err && <Card className="mt-6 p-8 text-center border-2 border-red-200" data-testid="receipt-error"><p className="font-bold text-red-700">{err}</p></Card>}
        {!data && !err && <p className="mt-8 text-sm text-zinc-500">Loading your receipt…</p>}
        {data && (
          <div className="mt-6 space-y-5">
            <div>
              <div className="font-tactical text-xs uppercase tracking-[0.25em] text-[#0C2E2B]/60">Proof of delivery · {data.ref_id}</div>
              <h1 className="font-display font-black text-4xl sm:text-5xl text-[#0C2E2B] mt-1 flex items-center gap-3"><FileSignature className="w-9 h-9" /> Delivered, {data.first_name}</h1>
              <p className="text-zinc-600 mt-2 text-sm md:text-base">{String(data.service_type || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())} · {data.preferred_date} · {data.address}, {data.zip_code}</p>
            </div>
            <Card className="p-5 border-2 border-[#0C2E2B]"><ReceiptBody receipt={data.receipt} /></Card>
            <Card className={`p-4 border-2 text-sm flex items-start gap-3 ${data.claim_window?.open ? "border-amber-300 bg-amber-50" : "border-zinc-200"}`} data-testid="receipt-claim-note">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-[#0C2E2B]">Something arrived damaged?</p>
                <p className="text-xs text-zinc-600 mt-0.5">{data.claim_window?.open ? `Report it with photos before ${new Date(data.claim_window.deadline).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} from your account.` : "The 48-hour claim window has closed, but you can still contact us and we'll take a look."} <Link to="/terms#delivery" className="underline font-semibold">Delivery terms</Link></p>
                <Button asChild size="sm" className="mt-2 rounded-md bg-[#0C2E2B] text-[#FFDF00] hover:bg-[#0C2E2B]/90 font-bold"><Link to="/account" data-testid="receipt-claim-link">Open my account</Link></Button>
              </div>
            </Card>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
